import React, { useState } from "react";
import type {
  ActiveLayer,
  Entity,
  Event,
  Weights,
} from "@nexus/contracts";
import { LayerFilters } from "./LayerFilters";
import { WeightSliders } from "./WeightSliders";
import { LAYER_METADATA } from "../types";
import { Search, Globe2, Building2 } from "lucide-react";

interface LeftSidebarProps {
  activeLayers: Set<ActiveLayer>;
  onToggleLayer: (layer: ActiveLayer) => void;
  onSelectAllLayers: () => void;
  onClearAllLayers: () => void;
  layerCounts: Record<ActiveLayer, number>;
  weights: Weights;
  onChangeWeights: (weights: Weights) => void;
  onResetWeights: () => void;
  isSimulating: boolean;
  entities: Entity[];
  events: Event[];
  selectedEntityId: string | null;
  onSelectEntity: (entityId: string) => void;
  onSelectEvent: (event: Event) => void;
}

export const LeftSidebar: React.FC<LeftSidebarProps> = ({
  activeLayers,
  onToggleLayer,
  onSelectAllLayers,
  onClearAllLayers,
  layerCounts,
  weights,
  onChangeWeights,
  onResetWeights,
  isSimulating,
  entities,
  events,
  selectedEntityId,
  onSelectEntity,
  onSelectEvent,
}) => {
  const [tab, setTab] = useState<"LAYERS" | "ENTITIES" | "EVENTS">("LAYERS");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredEntities = entities.filter(
    (e) =>
      e.nameKo.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.nameEn.toLowerCase().includes(searchQuery.toLowerCase()) ||
      e.id.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const filteredEvents = events.filter((ev) => {
    const layerMatch = ev.layers.some((l) => activeLayers.has(l as ActiveLayer));
    const textMatch =
      !searchQuery ||
      ev.titleKo.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ev.titleEn.toLowerCase().includes(searchQuery.toLowerCase());
    return layerMatch && textMatch;
  });

  return (
    <aside className="floating-panel left-panel">
      {/* Sub Tabs */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, 1fr)",
          background: "#090f1d",
          borderBottom: "1px solid var(--border-subtle)",
        }}
      >
        <button
          className={`btn-secondary ${tab === "LAYERS" ? "active" : ""}`}
          onClick={() => setTab("LAYERS")}
          style={{
            borderRadius: 0,
            border: "none",
            borderBottom: tab === "LAYERS" ? "2px solid var(--accent-indigo)" : "2px solid transparent",
            background: tab === "LAYERS" ? "rgba(99, 102, 241, 0.15)" : "transparent",
            color: tab === "LAYERS" ? "#ffffff" : "var(--text-secondary)",
            padding: "11px 0",
            fontWeight: 700,
            fontSize: "12px",
          }}
        >
          레이어 & 가중치
        </button>
        <button
          className={`btn-secondary ${tab === "ENTITIES" ? "active" : ""}`}
          onClick={() => setTab("ENTITIES")}
          style={{
            borderRadius: 0,
            border: "none",
            borderBottom: tab === "ENTITIES" ? "2px solid var(--accent-cyan)" : "2px solid transparent",
            background: tab === "ENTITIES" ? "rgba(6, 182, 212, 0.15)" : "transparent",
            color: tab === "ENTITIES" ? "#ffffff" : "var(--text-secondary)",
            padding: "11px 0",
            fontWeight: 700,
            fontSize: "12px",
          }}
        >
          국가/기업 ({entities.length})
        </button>
        <button
          className={`btn-secondary ${tab === "EVENTS" ? "active" : ""}`}
          onClick={() => setTab("EVENTS")}
          style={{
            borderRadius: 0,
            border: "none",
            borderBottom: tab === "EVENTS" ? "2px solid var(--accent-rose)" : "2px solid transparent",
            background: tab === "EVENTS" ? "rgba(244, 63, 94, 0.15)" : "transparent",
            color: tab === "EVENTS" ? "#ffffff" : "var(--text-secondary)",
            padding: "11px 0",
            fontWeight: 700,
            fontSize: "12px",
          }}
        >
          사건 피드 ({filteredEvents.length})
        </button>
      </div>

      {/* Tab 1: Layer Filters & Weight Sliders */}
      {tab === "LAYERS" && (
        <>
          <LayerFilters
            activeLayers={activeLayers}
            onToggleLayer={onToggleLayer}
            onSelectAll={onSelectAllLayers}
            onClearAll={onClearAllLayers}
            layerCounts={layerCounts}
          />
          <WeightSliders
            weights={weights}
            onChange={onChangeWeights}
            onReset={onResetWeights}
            isSimulating={isSimulating}
          />
        </>
      )}

      {/* Tab 2: Entities Dossier Explorer */}
      {tab === "ENTITIES" && (
        <div style={{ padding: "14px", flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ position: "relative", marginBottom: "10px" }}>
            <Search
              size={14}
              style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "var(--text-secondary)" }}
            />
            <input
              type="text"
              placeholder="국가/기업/기관 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                background: "rgba(19, 29, 51, 0.7)",
                border: "1px solid var(--border-medium)",
                borderRadius: "6px",
                padding: "8px 10px 8px 32px",
                color: "#ffffff",
                fontSize: "12px",
                outline: "none",
              }}
            />
          </div>

          <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "6px" }}>
            {filteredEntities.map((entity) => {
              const isSelected = selectedEntityId === entity.id;
              return (
                <div
                  key={entity.id}
                  style={{
                    background: isSelected ? "rgba(56, 189, 248, 0.14)" : "rgba(19, 29, 51, 0.5)",
                    border: `1px solid ${isSelected ? "var(--accent-cyan)" : "var(--border-subtle)"}`,
                    borderRadius: "6px",
                    padding: "9px 11px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    transition: "all 0.15s ease",
                  }}
                  onClick={() => onSelectEntity(entity.id)}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                    {entity.type === "company" ? (
                      <Building2 size={16} color="var(--accent-violet)" />
                    ) : (
                      <Globe2 size={16} color="var(--accent-cyan)" />
                    )}
                    <div>
                      <div style={{ fontSize: "13px", fontWeight: 700, color: isSelected ? "#38bdf8" : "#ffffff" }}>
                        {entity.nameKo} <span style={{ fontSize: "11px", color: "var(--text-secondary)", fontWeight: 500 }}>({entity.nameEn})</span>
                      </div>
                      <div className="font-mono" style={{ fontSize: "11px", color: "var(--text-secondary)", marginTop: "2px" }}>
                        {entity.type.toUpperCase()} · [{entity.lat.toFixed(1)}°, {entity.lon.toFixed(1)}°]
                      </div>
                    </div>
                  </div>

                  {entity.indexEligible && (
                    <span
                      style={{
                        fontSize: "10px",
                        color: "var(--accent-cyan)",
                        background: "rgba(6, 182, 212, 0.2)",
                        border: "1px solid rgba(6, 182, 212, 0.4)",
                        padding: "2px 6px",
                        borderRadius: "3px",
                        fontWeight: 700,
                      }}
                    >
                      지수국가
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Tab 3: Events Stream */}
      {tab === "EVENTS" && (
        <div style={{ padding: "14px", flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
          <div style={{ position: "relative", marginBottom: "10px" }}>
            <Search
              size={14}
              style={{ position: "absolute", left: "10px", top: "50%", transform: "translateY(-50%)", color: "var(--text-secondary)" }}
            />
            <input
              type="text"
              placeholder="사건 검색..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                background: "rgba(19, 29, 51, 0.7)",
                border: "1px solid var(--border-medium)",
                borderRadius: "6px",
                padding: "8px 10px 8px 32px",
                color: "#ffffff",
                fontSize: "12px",
                outline: "none",
              }}
            />
          </div>

          <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: "8px" }}>
            {filteredEvents.map((ev) => {
              const primaryLayer = ev.layers.find((l) => activeLayers.has(l as ActiveLayer)) as
                | ActiveLayer
                | undefined;
              const meta = primaryLayer ? LAYER_METADATA[primaryLayer] : undefined;

              return (
                <div
                  key={ev.id}
                  style={{
                    background: "rgba(19, 29, 51, 0.5)",
                    border: "1px solid var(--border-subtle)",
                    borderRadius: "6px",
                    padding: "11px",
                    cursor: "pointer",
                    borderLeft: `3px solid ${meta?.color || "var(--accent-indigo)"}`,
                    transition: "all 0.15s ease",
                  }}
                  onClick={() => onSelectEvent(ev)}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "4px" }}>
                    <span style={{ fontSize: "13px", fontWeight: 700, color: "#ffffff" }}>
                      {ev.titleKo}
                    </span>
                    <span className="font-mono" style={{ fontSize: "11px", color: "var(--accent-rose)", fontWeight: 700 }}>
                      SEV-{ev.severity}
                    </span>
                  </div>

                  <p style={{ fontSize: "12px", color: "var(--text-secondary)", lineHeight: 1.4, marginBottom: "8px" }}>
                    {ev.summaryKo}
                  </p>

                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: "11px", color: "var(--text-muted)" }}>
                    <span className="font-mono" style={{ color: "var(--text-secondary)" }}>{new Date(ev.occurredAt).toLocaleDateString()}</span>
                    <div style={{ display: "flex", gap: "4px" }}>
                      {ev.layers.map((l) => (
                        <span
                          key={l}
                          style={{
                            fontSize: "10px",
                            fontWeight: 600,
                            padding: "1px 5px",
                            background: "rgba(255,255,255,0.08)",
                            borderRadius: "3px",
                            color: "#e2e8f0",
                          }}
                        >
                          {l}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </aside>
  );
};
